import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import {
  getFLNLearnerState, getFLNItems,
  getActiveFLNAssessment, respondFLNAssessment, completeFLNAssessment,
} from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, BookOpen, PenLine, Hash, AlertTriangle,
  ChevronRight, CheckCircle2, XCircle, Loader2, Library,
  RotateCcw, ClipboardCheck,
} from "lucide-react";

// ─── Competency maps ────────────────────────────────────────────────────────
const READING_COMPETENCIES = {
  R1: "Read alphabets",
  R2: "Read 3-letter words",
  R3: "Read 4-letter words",
  R4: "Read complex words",
  R5: "Read simple sentences (3-letter words)",
  R6: "Read simple sentences (4-letter words)",
  R7: "Read small paragraph",
  R8: "Read paragraph with tone and intonation",
  R9: "Read paragraph with punctuation awareness",
  R10: "Comprehension reading",
};
const WRITING_COMPETENCIES = {
  W1: "Write alphabets",
  W2: "Write 3-letter words",
  W3: "Write 4-letter words",
  W4: "Write complex words",
  W5: "Recognise and write digraphs",
  W6: "Write words with digraphs",
  W7: "Write simple sentences",
  W8: "Write sentences with blends",
  W9: "Write sentences with digraphs",
  W10: "Write long and complex sentences",
  W11: "Paragraph writing",
  W12: "Punctuation in writing",
  W13: "Comprehension — prose and poem",
};
const COUNTING_COMPETENCIES = {
  C1: "Identify numbers 1–50",
  C2: "Identify numbers 51–100",
  C3: "Identify numbers 100–999",
  C4: "Addition without carry",
  C5: "Addition with carry",
  C6: "Subtraction without borrow",
  C7: "Subtraction with borrow",
  C8: "Tables 1–15",
  C9: "Multiplication by 1 digit",
  C10: "Multiplication by 2 digits",
  C11: "Division without remainder",
  C12: "Division with remainder",
  C13: "Fractions — basic",
  C14: "Fractions — operations",
  C15: "Decimals — basic",
  C16: "Decimals — operations",
  C17: "Mixed operations",
  C18: "Word problems",
};

const COMPETENCY_MAP = {
  reading: READING_COMPETENCIES,
  writing: WRITING_COMPETENCIES,
  counting: COUNTING_COMPETENCIES,
};

// ─── Tab config ─────────────────────────────────────────────────────────────
const TABS = [
  { key: "reading", label: "Reading",  Icon: BookOpen, color: "teal" },
  { key: "writing", label: "Writing",  Icon: PenLine,  color: "teal" },
  { key: "counting",label: "Counting", Icon: Hash,     color: "teal" },
];

// ─── Shuffle helper (Fisher-Yates) ──────────────────────────────────────────
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildOptions(item) {
  return shuffle([item.answer, ...item.distractors]);
}

// ─── Practice flow ───────────────────────────────────────────────────────────
function PracticeFlow({ head, items, onDone }) {
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [options, setOptions] = useState([]);

  const currentItem = items[index];
  const isLast = index === items.length - 1;
  const isCounting = head === "counting";

  useEffect(() => {
    if (isCounting && currentItem) {
      setOptions(buildOptions(currentItem));
      setSelected(null);
    }
  }, [index, isCounting, currentItem]);

  const handleNext = () => {
    if (isLast) {
      onDone();
    } else {
      setIndex(i => i + 1);
      setSelected(null);
    }
  };

  if (!currentItem) return null;

  const progress = Math.round(((index + 1) / items.length) * 100);

  return (
    <div className="space-y-5" data-testid="fln-practice-flow">
      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-teal-500 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
          {index + 1} / {items.length}
        </span>
      </div>

      {/* Item card */}
      <Card className="rounded-2xl p-6 border border-teal-100 bg-white shadow-sm">
        <div className="mb-1">
          <span className="text-[10px] font-bold uppercase tracking-widest text-teal-500">
            {currentItem.level} · {currentItem.type}
          </span>
        </div>

        {isCounting ? (
          /* ── Counting MCQ ── */
          <div className="space-y-4">
            <p className="text-xl font-semibold text-slate-800 leading-relaxed" data-testid="fln-counting-question">
              {currentItem.question}
            </p>
            <div className="grid grid-cols-2 gap-3" data-testid="fln-counting-options">
              {options.map((opt, i) => {
                const isCorrect = opt === currentItem.answer;
                const isSelected = selected === opt;
                let cls = "rounded-xl p-3 text-center font-medium border-2 transition-all cursor-pointer text-sm ";
                if (!selected) {
                  cls += "border-slate-200 hover:border-teal-400 hover:bg-teal-50 text-slate-700";
                } else if (isCorrect) {
                  cls += "border-emerald-400 bg-emerald-50 text-emerald-800";
                } else if (isSelected) {
                  cls += "border-red-400 bg-red-50 text-red-800";
                } else {
                  cls += "border-slate-100 bg-slate-50 text-slate-400 cursor-default";
                }
                return (
                  <button
                    key={i}
                    className={cls}
                    onClick={() => !selected && setSelected(opt)}
                    data-testid={`fln-option-${i}`}
                    disabled={!!selected && !isCorrect && !isSelected}
                  >
                    <span className="text-xs text-slate-400 font-normal mr-1">
                      {String.fromCharCode(65 + i)}.
                    </span>
                    {opt}
                    {selected && isCorrect && (
                      <CheckCircle2 className="inline w-4 h-4 ml-1 text-emerald-500" />
                    )}
                    {selected && isSelected && !isCorrect && (
                      <XCircle className="inline w-4 h-4 ml-1 text-red-500" />
                    )}
                  </button>
                );
              })}
            </div>
            {selected && (
              <p className={`text-sm font-medium ${selected === currentItem.answer ? "text-emerald-700" : "text-red-600"}`} data-testid="fln-answer-feedback">
                {selected === currentItem.answer
                  ? "Correct!"
                  : `The correct answer is: ${currentItem.answer}`}
              </p>
            )}
          </div>
        ) : (
          /* ── Reading / Writing ── */
          <div className="space-y-4">
            {head === "writing" && currentItem.teacher_prompt && (
              <p className="text-xs text-slate-500 italic border-l-2 border-teal-200 pl-3">
                {currentItem.teacher_prompt}
              </p>
            )}
            {currentItem.content ? (
              <p className="text-2xl font-semibold text-slate-800 leading-relaxed" data-testid="fln-rw-content">
                {currentItem.content}
              </p>
            ) : currentItem.source_item_id ? (
              <p className="text-sm text-slate-500 italic" data-testid="fln-rw-content">
                [Refers to {currentItem.source_item_id}] — use the paragraph card provided by your teacher.
                {currentItem.evaluation_criteria && (
                  <span className="block mt-1 font-medium text-slate-600">
                    Focus: {currentItem.evaluation_criteria}
                  </span>
                )}
              </p>
            ) : null}
            {head === "reading" && currentItem.questions && (
              <div className="space-y-3 mt-2">
                {currentItem.questions.map((q, qi) => (
                  <div key={qi} className="bg-slate-50 rounded-xl p-3">
                    <p className="text-sm font-medium text-slate-700 mb-2">{qi + 1}. {q.question}</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {Object.entries(q.options).map(([k, v]) => (
                        <div key={k} className={`rounded-lg px-2.5 py-1.5 text-xs ${k === q.correct ? "bg-emerald-100 text-emerald-800 font-semibold" : "bg-white text-slate-600 border border-slate-100"}`}>
                          <span className="font-bold mr-1">{k}.</span>{v}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Next button — for counting, only after selecting; for R/W, always */}
      {(isCounting ? !!selected : true) && (
        <Button
          onClick={handleNext}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl tap-44"
          data-testid="fln-next-btn"
        >
          {isLast ? "Finish" : "Next"}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
}

// ─── Done screen ─────────────────────────────────────────────────────────────
function PracticeDone({ head, level, onRestart, onBack }) {
  return (
    <div className="text-center space-y-6 py-8" data-testid="fln-practice-done">
      <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-8 h-8 text-teal-600" />
      </div>
      <div>
        <h3 className="font-display text-xl font-bold text-slate-800">Well done!</h3>
        <p className="text-slate-500 text-sm mt-1">
          You've completed all practice items for {level}.
        </p>
      </div>
      <div className="flex gap-3 justify-center">
        <Button variant="outline" onClick={onRestart} className="rounded-xl tap-44 gap-2" data-testid="fln-restart-btn">
          <RotateCcw className="w-4 h-4" /> Practice again
        </Button>
        <Button onClick={onBack} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl tap-44" data-testid="fln-back-to-tab-btn">
          Back
        </Button>
      </div>
    </div>
  );
}

// ─── Student Device Assessment flow ─────────────────────────────────────────
/**
 * Renders the in-session assessment from the student side.
 * Counting: MCQ + auto-mark (calls respond per item, complete on last).
 * Reading/Writing: display only (teacher marks from dashboard).
 */
function StudentAssessmentFlow({ session, onComplete }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [selected, setSelected]     = useState(null);
  const [options, setOptions]       = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const items      = session.item_details || [];
  const head       = session.head;
  const isCounting = head === "counting";
  const item       = items[currentIdx];
  const isLast     = currentIdx === items.length - 1;

  useEffect(() => {
    if (isCounting && item?.distractors) {
      setOptions(shuffle([item.answer, ...item.distractors]));
      setSelected(null);
    }
  }, [currentIdx, isCounting, item]);

  const handleCountingSelect = async (opt) => {
    if (selected) return;
    setSelected(opt);
    try {
      await respondFLNAssessment(session.session_id, item.item_id, opt === item.answer);
    } catch {}
  };

  const handleNext = async () => {
    if (isLast) {
      setSubmitting(true);
      if (isCounting) {
        try { await completeFLNAssessment(session.session_id); } catch {}
      }
      setSubmitting(false);
      onComplete();
    } else {
      setCurrentIdx((i) => i + 1);
      setSelected(null);
    }
  };

  if (!item) return null;

  const progress = Math.round(((currentIdx + 1) / items.length) * 100);
  const canNext  = isCounting ? !!selected : true;

  return (
    <div className="space-y-5" data-testid="fln-assessment-flow">
      {/* Assessment badge */}
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
        <ClipboardCheck className="w-3 h-3" /> Assessment
      </div>

      {/* Progress */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
          {currentIdx + 1} / {items.length}
        </span>
      </div>

      {/* Item card */}
      <Card className="rounded-2xl p-6 border border-blue-100 bg-white shadow-sm">
        <div className="mb-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-blue-400">
            {item.level} · Assessment
          </span>
        </div>
        {isCounting ? (
          <div className="space-y-4">
            <p className="text-xl font-semibold text-slate-800 leading-relaxed" data-testid="fln-assess-question">
              {item.question}
            </p>
            <div className="grid grid-cols-2 gap-3" data-testid="fln-assess-options">
              {options.map((opt, i) => {
                const isCorrect  = opt === item.answer;
                const isSelected = selected === opt;
                let cls = "rounded-xl p-3 text-center font-medium border-2 transition-all text-sm cursor-pointer ";
                if (!selected) {
                  cls += "border-slate-200 hover:border-blue-400 hover:bg-blue-50 text-slate-700";
                } else if (isCorrect) {
                  cls += "border-emerald-400 bg-emerald-50 text-emerald-800";
                } else if (isSelected) {
                  cls += "border-red-400 bg-red-50 text-red-800";
                } else {
                  cls += "border-slate-100 bg-slate-50 text-slate-400 cursor-default";
                }
                return (
                  <button
                    key={i}
                    className={cls}
                    onClick={() => handleCountingSelect(opt)}
                    disabled={!!selected && !isCorrect && !isSelected}
                    data-testid={`fln-assess-opt-${i}`}
                  >
                    <span className="text-xs text-slate-400 font-normal mr-1">
                      {String.fromCharCode(65 + i)}.
                    </span>
                    {opt}
                    {selected && isCorrect && <CheckCircle2 className="inline w-4 h-4 ml-1 text-emerald-500" />}
                    {selected && isSelected && !isCorrect && <XCircle className="inline w-4 h-4 ml-1 text-red-500" />}
                  </button>
                );
              })}
            </div>
            {selected && (
              <p className={`text-sm font-medium ${selected === item.answer ? "text-emerald-700" : "text-red-600"}`} data-testid="fln-assess-feedback">
                {selected === item.answer ? "Correct!" : `Correct answer: ${item.answer}`}
              </p>
            )}
          </div>
        ) : (
          /* Reading / Writing — display only */
          <div className="space-y-3">
            {head === "writing" && item.teacher_prompt && (
              <p className="text-xs text-slate-500 italic border-l-2 border-blue-200 pl-3">
                {item.teacher_prompt}
              </p>
            )}
            <p className="text-2xl font-semibold text-slate-800 leading-relaxed" data-testid="fln-assess-rw-content">
              {item.content || "(No content)"}
            </p>
            <p className="text-xs text-blue-500 font-medium">
              Your teacher is observing and recording your response.
            </p>
          </div>
        )}
      </Card>

      {/* Next / Submit */}
      {canNext && (
        <Button
          onClick={handleNext}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl tap-44 gap-1"
          data-testid="fln-assess-next-btn"
          disabled={submitting}
        >
          {submitting
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : isLast
            ? "Submit Assessment"
            : <><span>Next</span><ChevronRight className="w-4 h-4" /></>}
        </Button>
      )}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function FLNModule() {
  const { learner } = useRole();
  const [activeTab, setActiveTab] = useState("reading");
  const [flnState, setFlnState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flnNotFound, setFlnNotFound] = useState(false);
  const [practiceState, setPracticeState] = useState(null); // null | { items, done }
  // Student device assessment
  const [tabAssessment, setTabAssessment] = useState(null);   // active session doc
  const [assessmentDone, setAssessmentDone] = useState(false); // student submitted their part

  const loadState = useCallback(async () => {
    if (!learner?.learner_id) return;
    try {
      const data = await getFLNLearnerState(learner.learner_id);
      setFlnState(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setFlnNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  }, [learner]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  // Check for in-progress student_device assessment for the current tab
  const checkTabAssessment = useCallback(async (head) => {
    if (!learner?.learner_id) return;
    setTabAssessment(null);
    setAssessmentDone(false);
    try {
      const data = await getActiveFLNAssessment(learner.learner_id, head);
      if (data.session) {
        // Skip sessions already completed by student this browser session
        const seen = (() => {
          try { return new Set(JSON.parse(sessionStorage.getItem("fln_seen_assessments") || "[]")); }
          catch { return new Set(); }
        })();
        if (!seen.has(data.session.session_id)) {
          setTabAssessment(data.session);
        }
      }
    } catch {}
  }, [learner?.learner_id]);

  useEffect(() => {
    checkTabAssessment(activeTab);
  }, [activeTab, checkTabAssessment]);

  // Exit practice
  const exitPractice = () => setPracticeState(null);

  // Called when student finishes assessment flow
  const handleAssessmentComplete = () => {
    if (tabAssessment) {
      const seen = (() => {
        try { return new Set(JSON.parse(sessionStorage.getItem("fln_seen_assessments") || "[]")); }
        catch { return new Set(); }
      })();
      seen.add(tabAssessment.session_id);
      sessionStorage.setItem("fln_seen_assessments", JSON.stringify([...seen]));
    }
    setTabAssessment(null);
    setAssessmentDone(true);
  };

  // Start practice for current tab
  const startPractice = async () => {
    const cursor = flnState?.[activeTab];
    if (!cursor) return;
    setPracticeState({ loading: true, items: [], done: false });
    try {
      const data = await getFLNItems(activeTab, cursor.current_level, "practice");
      setPracticeState({ loading: false, items: data.items, done: false });
    } catch {
      setPracticeState(null);
    }
  };

  const tabCursor = flnState?.[activeTab];
  const competency = tabCursor
    ? COMPETENCY_MAP[activeTab]?.[tabCursor.current_level] || "Level details coming soon"
    : null;

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex justify-center items-center py-32">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  if (flnNotFound) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/home"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-teal-600 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <Card className="rounded-2xl p-8 border border-amber-200 bg-amber-50 text-center" data-testid="fln-not-found">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h3 className="font-display text-xl font-bold text-slate-800 mb-2">FLN profile not set up</h3>
          <p className="text-sm text-amber-700">
            Your FLN learning profile hasn't been initialised. Please ask your teacher or school admin to check your account.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="fln-module-page">
      {/* Back */}
      <Link
        to="/home"
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-teal-600 transition-colors mb-8"
        data-testid="fln-back-home"
      >
        <ArrowLeft className="w-4 h-4" /> Back to home
      </Link>

      {/* Header */}
      <div className="flex items-center gap-4 mb-2">
        <div className="w-14 h-14 rounded-2xl bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0">
          <Library className="w-7 h-7" />
        </div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-800" data-testid="fln-title">
            Foundational Literacy & Numeracy
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Reading · Writing · Counting</p>
        </div>
      </div>

      <p className="text-slate-500 text-sm mb-8 ml-[4.5rem]">
        Build strong foundations — one level at a time.
      </p>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-8" data-testid="fln-tabs">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => { setActiveTab(key); setPracticeState(null); setAssessmentDone(false); }}
            data-testid={`fln-tab-${key}`}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              activeTab === key
                ? "bg-white text-teal-700 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{label}</span>
            <span className="sm:hidden">{label.slice(0, 3)}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tabAssessment && !assessmentDone ? (
        /* Student device assessment in progress */
        <StudentAssessmentFlow
          session={tabAssessment}
          onComplete={handleAssessmentComplete}
        />
      ) : assessmentDone ? (
        /* Student submitted — waiting for teacher */
        <div className="text-center space-y-5 py-10" data-testid="fln-assessment-submitted">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
            <ClipboardCheck className="w-8 h-8 text-blue-500" />
          </div>
          <div>
            <h3 className="font-display text-xl font-bold text-slate-800">Assessment submitted!</h3>
            <p className="text-slate-500 text-sm mt-1">
              Your teacher will review and record the result.
            </p>
          </div>
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={() => setAssessmentDone(false)}
            data-testid="fln-assess-return-btn"
          >
            Return to practice
          </Button>
        </div>
      ) : practiceState?.loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-7 h-7 text-teal-500 animate-spin" />
        </div>
      ) : practiceState?.done ? (
        <PracticeDone
          head={activeTab}
          level={tabCursor?.current_level}
          onRestart={() => setPracticeState({ ...practiceState, done: false })}
          onBack={exitPractice}
        />
      ) : practiceState?.items ? (
        <div>
          <button
            onClick={exitPractice}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-teal-600 mb-5 transition-colors"
            data-testid="fln-exit-practice"
          >
            <ArrowLeft className="w-3 h-3" /> Back to {activeTab}
          </button>
          {practiceState.items.length === 0 ? (
            <Card className="rounded-2xl p-8 border border-slate-100 text-center" data-testid="fln-no-items">
              <p className="text-slate-500 text-sm">
                No practice items available for {tabCursor?.current_level} yet.
                Please check with your teacher.
              </p>
              <Button variant="outline" onClick={exitPractice} className="mt-4 rounded-xl" data-testid="fln-go-back-empty">
                Go back
              </Button>
            </Card>
          ) : (
            <PracticeFlow
              key={`${activeTab}-${tabCursor?.current_level}`}
              head={activeTab}
              items={practiceState.items}
              onDone={() => setPracticeState(s => ({ ...s, done: true }))}
            />
          )}
        </div>
      ) : (
        /* Hub view */
        <TabHub
          head={activeTab}
          cursor={tabCursor}
          competency={competency}
          onStartPractice={startPractice}
        />
      )}
    </div>
  );
}

// ─── Tab hub card ─────────────────────────────────────────────────────────────
function TabHub({ head, cursor, competency, onStartPractice }) {
  const isHeld = cursor?.cursor_status === "held";
  const level = cursor?.current_level;

  return (
    <div className="space-y-5" data-testid={`fln-tab-content-${head}`}>
      {/* Level card */}
      <Card className="rounded-2xl p-7 border border-teal-100 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-teal-600 text-white flex items-center justify-center flex-shrink-0 shadow-md">
            <span className="font-display text-lg font-black tracking-tight" data-testid={`fln-level-badge-${head}`}>
              {level}
            </span>
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-wider text-teal-600 mb-1">Current Level</p>
            <h2 className="font-display text-xl font-bold text-slate-800" data-testid={`fln-competency-${head}`}>
              {competency}
            </h2>
            <p className="text-sm text-slate-500 mt-1 capitalize">{head}</p>
          </div>
        </div>
      </Card>

      {/* Held warning */}
      {isHeld && (
        <Card className="rounded-2xl p-5 border border-amber-200 bg-amber-50" data-testid={`fln-held-message-${head}`}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              Your teacher has paused this level. Please check with your teacher.
            </p>
          </div>
        </Card>
      )}

      {/* Practice button */}
      <Button
        onClick={onStartPractice}
        disabled={isHeld}
        className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl tap-44 text-base font-semibold"
        data-testid={`fln-practice-btn-${head}`}
      >
        Practice {level}
        <ChevronRight className="w-5 h-5 ml-1" />
      </Button>
    </div>
  );
}
